/**
 * Postcode Extraction Utility
 * Extracts Japanese postcode from purchase confirmation images using OCR
 * and validates against warehouse addresses
 */

import { createWorker } from 'tesseract.js';

// Warehouse postcodes configuration
export const WAREHOUSE_POSTCODES = {
  ship: '231-0023',   // Ship cargo warehouse (เรือ)
  air: '270-1112',    // Air cargo warehouse (แอร์)
};

// Matches Japanese postcodes with or without spaces around the dash:
// 〒349-0225 / 〒349 - 0225 / 349-0225 / 188 - 0014
const POSTCODE_REGEX = /[〒〒]?\s*(\d{3})\s*-\s*(\d{4})/;

// Matches the family keyword anywhere in a line (case-insensitive word boundary).
// Covers both the original "TAMA Chawin733" and Google-Translated "โต้ะ TAMA หมายเลข 135".
const SMILE_KEYWORD_REGEX = /\b(smile|tama|oda|anny)\b/i;

/**
 * Extract the order number from any smileName-related text.
 *
 * Priority:
 *   1. Number directly attached to a Latin word: "Wichaiyut12" → "12"
 *      (most reliable — OCR stray digits won't interfere)
 *   2. Last standalone number on the line (fallback for translated format
 *      like "โต้ะ TAMA หมายเลข 135" where there's no Latin+digit word)
 *
 * Examples:
 *   "Tama Wichaiyut12"               → "12"
 *   "記 呈 けり Tama Wichaiyut12 2"  → "12"  (stray trailing 2 ignored)
 *   "TAMA Chawin733"                  → "733"
 *   "โต้ะ TAMA หมายเลข 135"          → "135"
 */
export const extractOrderNumber = (text) => {
  if (!text) return null;

  // Priority 1: number glued onto a Latin word (e.g. "Wichaiyut12", "Chawin733")
  const attachedMatches = [...text.matchAll(/[A-Za-z]+(\d+)/g)];
  if (attachedMatches.length > 0) {
    // Take the last such match (the surname+number is typically last)
    return attachedMatches[attachedMatches.length - 1][1];
  }

  // Priority 2: last standalone number (translated format "หมายเลข 135")
  const lastNumber = text.match(/(\d+)\s*$/);
  return lastNumber ? lastNumber[1] : null;
};

/**
 * Extract the smileName line from a single OCR line.
 *
 * Handles three formats seen in Mercari purchase confirmation images:
 *   A) Name on its own line:          Tama Thosathit189
 *   B) Name inside parentheses:       1F (TAMA Chawin733)
 *   C) Google-Translated line:        โต้ะ TAMA หมายเลข 135
 *
 * The line must contain one of the family keywords (tama/smile/oda/anny).
 *
 * @param {string} line
 * @returns {string|null}
 */
const extractNameFromLine = (line) => {
  // Format B: grab the content inside parentheses if it contains the keyword
  const parenMatch = line.match(/\(([^)]*)\)/);
  if (parenMatch && SMILE_KEYWORD_REGEX.test(parenMatch[1])) {
    return parenMatch[1].trim();
  }

  // Formats A & C: keyword appears anywhere in the line
  if (SMILE_KEYWORD_REGEX.test(line)) return line.trim();

  return null;
};

/**
 * Extract the Latin recipient name (smileName) from OCR full text.
 *
 * Mercari purchase confirmation images show the delivery address block.
 * After the postcode line, we look for the first line that contains a
 * Latin name — either directly or inside parentheses.
 *
 * Examples:
 *   〒349-0225                        〒188 - 0014
 *   埼玉県 白岡市 太田 新井685-10      3-30-8 ชิบาคุโบะ-โชะ, โตเกียว
 *   Tama Thosathit189       OR        1F (TAMA Chawin733)
 *   Yama Piraya 等                    ยามะพิรัญ
 *
 * @param {string} fullText - OCR text from the entire image
 * @returns {string|null} extracted name, or null if not found
 */
export const extractSmileNameFromText = (fullText) => {
  if (!fullText) return null;

  const lines = fullText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

  // Find the first line that contains a postcode (allowing spaces around dash)
  const postcodeLineIdx = lines.findIndex(l => POSTCODE_REGEX.test(l));

  if (postcodeLineIdx !== -1) {
    // First try the postcode line itself (OCR can merge lines) and lines after it
    for (let i = postcodeLineIdx; i < lines.length; i++) {
      const name = extractNameFromLine(lines[i]);
      if (name) return name;
    }
  }

  // Fallback: scan the entire text (handles cases where OCR reorders lines)
  for (const line of lines) {
    const name = extractNameFromLine(line);
    if (name) return name;
  }

  return null;
};

/**
 * Extract Mercari item number from the order URL.
 *   https://jp.mercari.com/item/m10544420653  →  "10544420653"
 */
export const extractItemNoFromUrl = (url) => {
  if (!url) return null;
  const match = url.match(/\/item\/[mM](\d+)/);
  return match ? match[1] : null;
};

/**
 * Extract Mercari item number from OCR full text.
 *
 * In the Thai Mercari UI the field looks like:
 *   รหัสสินค้า   ม10544420653   สำเนา
 *
 * Tesseract (jpn model) may read ม as m, n, N, w, ท, น, etc., but the
 * 11-digit number itself is always preserved.
 *
 * Strategy: look for a letter/symbol directly followed by exactly 11 digits.
 * Fall back to any standalone 11-digit sequence.
 */
export const extractItemNoFromText = (fullText) => {
  if (!fullText) return null;
  // ม (Thai) or common OCR variants followed by 11 digits
  const prefixed = fullText.match(/[มmMnNwทณกขค]\s*(\d{11})/);
  if (prefixed) return prefixed[1];
  // standalone 11-digit number (not part of a longer sequence)
  const standalone = fullText.match(/(?<!\d)(\d{11})(?!\d)/);
  return standalone ? standalone[1] : null;
};

/**
 * Extract postcode, smileName and itemNo from purchase confirmation image (single OCR pass).
 */
export const extractPostcodeFromImage = async (imageFile) => {
  const startTime = performance.now();
  let worker = null;

  try {
    // jpn+eng: Japanese for postcode/kanji, English for Latin names (ODA, TAMA, Latamine…)
    worker = await createWorker('jpn+eng');
    const { data: { text } } = await worker.recognize(imageFile);
    
    const match = text.match(POSTCODE_REGEX);
    const postcode = match ? `${match[1]}-${match[2]}` : null;
    const smileName = extractSmileNameFromText(text);
    const itemNo = extractItemNoFromText(text);

    console.log('[OCR] postcode:', postcode, '| smileName:', smileName, '| itemNo:', itemNo);

    return {
      postcode,
      smileName,
      itemNo,
      processingTime: Math.round(performance.now() - startTime),
      fullText: text,
      error: null
    };
  } catch (error) {
    console.error('Error extracting postcode:', error);
    return {
      postcode: null,
      smileName: null,
      itemNo: null,
      processingTime: Math.round(performance.now() - startTime),
      fullText: '',
      error: error.message || 'Failed to extract postcode'
    };
  } finally {
    if (worker) await worker.terminate();
  }
};

/**
 * Extract a Japanese postcode from any free-text address string.
 * Same regex used in extractPostcodeFromImage, exported for use elsewhere.
 */
export const extractPostcodeFromAddress = (address) => {
  if (!address) return null;
  const m = address.match(POSTCODE_REGEX);
  return m ? `${m[1]}-${m[2]}` : null;
};

/**
 * Validate purchase confirmation image against expected warehouse, smileName, and item number.
 *
 * @param {File|Blob} imageFile          - The image file to process
 * @param {boolean}   isHandCarry        - true = Air, false = Ship (selects the warehouse postcode)
 * @param {string|null} expectedSmileName  - Full recipient name (smileName + orderNo)
 * @param {*} _suggestedWarehouse         - Deprecated: kept for call-site compatibility; no longer used
 * @param {string|null} expectedItemNo   - 11-digit item number from the order URL
 * @param {{ skipSmileNameOcrCheck?: boolean }} [options]
 * @returns {Promise<Object>}
 */
export const validatePurchaseConfirmImage = async (
  imageFile,
  isHandCarry,
  expectedSmileName = null,
  _suggestedWarehouse = null,
  expectedItemNo = null,
  options = {},
) => {
  const { skipSmileNameOcrCheck = false } = options;
  // Expected postcode comes from the fixed warehouse constants, selected by isHandCarry.
  // Only two warehouses are in use (air = Chiba, sea = Yokohama); suggestedWarehouse is
  // no longer used to derive the expected postcode.
  const expectedType = isHandCarry ? 'air' : 'ship';
  const expectedPostcode = WAREHOUSE_POSTCODES[expectedType];
  const expectedLabel = isHandCarry ? 'Air (แอร์)' : 'Ship (เรือ)';
  
  // Single OCR pass — extracts postcode, smileName, and itemNo
  const extraction = await extractPostcodeFromImage(imageFile);
  const extractedSmileName = extraction.smileName || null;
  const extractedItemNo    = extraction.itemNo    || null;

  // ── Item number check (most critical — indicates a completely wrong image) ──
  const itemNoMismatch =
    !!expectedItemNo && !!extractedItemNo && extractedItemNo !== expectedItemNo;

  // ── Order number check (compare trailing digits only; robust against Google Translate) ──
  const extractedOrderNo = extractOrderNumber(extractedSmileName);
  const expectedOrderNo  = extractOrderNumber(expectedSmileName);
  const nameIsMatch = skipSmileNameOcrCheck ||
    !expectedSmileName ||
    !extractedSmileName ||
    !extractedOrderNo  ||
    !expectedOrderNo   ||
    extractedOrderNo === expectedOrderNo;
  const nameMismatchMessage = skipSmileNameOcrCheck
    ? null
    : (!nameIsMatch
      ? `หมายเลขออเดอร์ในรูปไม่ตรง: พบ ${extractedOrderNo} — ควรเป็น ${expectedOrderNo} (${expectedSmileName})`
      : null);

  // Shared base fields added to every return value
  const itemNoFields = { itemNoMismatch, extractedItemNo, expectedItemNo };

  // If extraction failed
  if (extraction.error) {
    return {
      isValid: false,
      cannotValidate: true,
      expectedPostcode,
      expectedLabel,
      extractedPostcode: null,
      extractedSmileName,
      ...itemNoFields,
      processingTime: extraction.processingTime,
      message: 'ไม่สามารถอ่านรูปภาพได้ กรุณาตรวจสอบด้วยตนเอง',
      messageEn: 'Could not read image. Please verify manually.',
      error: extraction.error
    };
  }
  
  // If no postcode found
  if (!extraction.postcode) {
    return {
      isValid: false,
      cannotValidate: true,
      expectedPostcode,
      expectedLabel,
      extractedPostcode: null,
      extractedSmileName,
      ...itemNoFields,
      processingTime: extraction.processingTime,
      message: 'ไม่พบรหัสไปรษณีย์ในรูปภาพ กรุณาตรวจสอบด้วยตนเอง',
      messageEn: 'No postcode found in image. Please verify manually.',
      fullText: extraction.fullText
    };
  }
  
  // Check if postcode matches expected warehouse
  const extractedPostcode = extraction.postcode;
  const isMatch = extractedPostcode === expectedPostcode;
  
  if (isMatch) {
    return {
      isValid: true,
      cannotValidate: false,
      expectedPostcode,
      expectedLabel,
      extractedPostcode,
      extractedSmileName,
      nameMismatchMessage,
      ...itemNoFields,
      processingTime: extraction.processingTime,
      message: `✓ รหัสไปรษณีย์ถูกต้อง: ${extractedPostcode} (${expectedLabel})`,
      messageEn: `✓ Postcode correct: ${extractedPostcode} (${expectedLabel})`,
      fullText: extraction.fullText
    };
  }
  
  // Postcode doesn't match
  const isShipPostcode = extractedPostcode === WAREHOUSE_POSTCODES.ship;
  const isAirPostcode  = extractedPostcode === WAREHOUSE_POSTCODES.air;
  
  let detailedMessage;
  let detailedMessageEn;

  // Specific air/ship swap messages when a known warehouse postcode is detected.
  if (isHandCarry && isShipPostcode) {
    detailedMessage =
      `⚠️ ผิดพลาด! ออเดอร์นี้เป็น Air (แอร์) แต่รูปแสดงที่อยู่โกดัง Ship (เรือ)\n` +
      `รหัสไปรษณีย์ในรูป : ${extractedPostcode}  ← โกดังเรือ\n` +
      `รหัสที่ควรจะเป็น  : ${expectedPostcode}  ← โกดังแอร์`;
    detailedMessageEn =
      `⚠️ ERROR! This order is Air but image shows Ship warehouse postcode!\n` +
      `Postcode in image : ${extractedPostcode}  (Ship warehouse)\n` +
      `Expected postcode : ${expectedPostcode}  (Air warehouse)`;
  } else if (!isHandCarry && isAirPostcode) {
    detailedMessage =
      `⚠️ ผิดพลาด! ออเดอร์นี้เป็น Ship (เรือ) แต่รูปแสดงที่อยู่โกดัง Air (แอร์)\n` +
      `รหัสไปรษณีย์ในรูป : ${extractedPostcode}  ← โกดังแอร์\n` +
      `รหัสที่ควรจะเป็น  : ${expectedPostcode}  ← โกดังเรือ`;
    detailedMessageEn =
      `⚠️ ERROR! This order is Ship but image shows Air warehouse postcode!\n` +
      `Postcode in image : ${extractedPostcode}  (Air warehouse)\n` +
      `Expected postcode : ${expectedPostcode}  (Ship warehouse)`;
  } else {
    detailedMessage =
      `⚠️ รหัสไปรษณีย์ไม่ตรง!\n` +
      `รหัสไปรษณีย์ในรูป : ${extractedPostcode}\n` +
      `รหัสที่ควรจะเป็น  : ${expectedPostcode} (${expectedLabel})`;
    detailedMessageEn =
      `⚠️ Postcode mismatch!\n` +
      `Postcode in image : ${extractedPostcode}\n` +
      `Expected postcode : ${expectedPostcode} (${expectedLabel})`;
  }
  
  return {
    isValid: false,
    cannotValidate: false,
    expectedPostcode,
    expectedLabel,
    extractedPostcode,
    extractedSmileName,
    nameMismatchMessage,
    ...itemNoFields,
    foundType: isShipPostcode ? 'ship' : (isAirPostcode ? 'air' : 'unknown'),
    foundLabel: isShipPostcode ? 'Ship (เรือ)' : (isAirPostcode ? 'Air (แอร์)' : 'Unknown'),
    processingTime: extraction.processingTime,
    message: detailedMessage,
    messageEn: detailedMessageEn,
    fullText: extraction.fullText
  };
};
